<template>
  <transition name="cs-fade">
    <div v-if="visible" class="cs-root" :class="{ 'cs-root--mobile': !isPc, 'cs-root--pc': isPc }">
      <div class="cs-panel">
        <header class="cs-header">
          <span class="cs-title">{{ $t('customerService.title') }}</span>
          <button type="button" class="cs-close" @click="close">×</button>
        </header>
        <div ref="messageList" class="cs-messages">
          <div v-if="loadingMore" class="cs-loading">{{ $t('customerService.loading') }}</div>
          <div
            v-for="msg in messages"
            :key="msg.id"
            class="cs-msg-row"
            :class="msg.senderType === 1 ? 'cs-msg-row--mine' : 'cs-msg-row--other'"
          >
            <div class="cs-bubble">
              <img
                v-if="msg.msgType === 'image'"
                :src="msg.content"
                class="cs-image"
                @click="previewImage(msg.content)"
              />
              <span v-else class="cs-text">{{ msg.content }}</span>
            </div>
            <div class="cs-time">{{ formatTime(msg.createTime) }}</div>
          </div>
        </div>
        <footer class="cs-footer">
          <label class="cs-upload-btn" :title="$t('customerService.sendImage')">
            <input type="file" accept="image/*" class="cs-file" @change="onImagePick" />
            <span>+</span>
          </label>
          <textarea
            v-model="draft"
            class="cs-input"
            rows="2"
            :placeholder="$t('customerService.placeholder')"
            @keydown.enter.exact.prevent="sendText"
          />
          <button type="button" class="cs-send" :disabled="sending" @click="sendText">
            {{ $t('customerService.send') }}
          </button>
        </footer>
      </div>
      <div v-if="!isPc" class="cs-backdrop" @click="close" />
    </div>
  </transition>
</template>

<script>
import { mapState } from 'vuex'
import { csBus } from '@/utils/csBus'
import { getToken } from '@/utils/auth'
import { VUE_APP_WS_URL } from '@/config'
import { getCsSession, getCsMessages, sendCsMessage, markCsRead } from '@/api/cs'
import request from '@/utils/request'
import Compressor from 'compressorjs'

export default {
  name: 'CustomerChat',
  data() {
    return {
      visible: false,
      session: null,
      messages: [],
      draft: '',
      sending: false,
      loadingMore: false,
      ws: null,
      pingTimer: null,
      reconnectTimer: null
    }
  },
  computed: {
    ...mapState(['isPc'])
  },
  mounted() {
    csBus.$on('open', this.open)
  },
  beforeDestroy() {
    csBus.$off('open', this.open)
    this.teardownWs()
  },
  methods: {
    async open() {
      this.visible = true
      try {
        const res = await getCsSession()
        if (res.code !== 200 && res.code !== 0) {
          throw new Error(res.message || 'session error')
        }
        this.session = res.result
        await this.loadMessages()
        this.connectWs()
        this.$nextTick(() => this.scrollBottom())
        if (this.session && this.session.id) {
          markCsRead({ sessionId: this.session.id })
        }
      } catch (e) {
        console.error(e)
        this.$message.error(this.$t('customerService.error'))
        this.visible = false
      }
    },
    close() {
      this.visible = false
      this.teardownWs()
    },
    async loadMessages(beforeId) {
      if (!this.session) return
      this.loadingMore = !!beforeId
      try {
        const res = await getCsMessages({
          sessionId: this.session.id,
          beforeId,
          limit: 50
        })
        const list = (res.result || [])
        if (beforeId) {
          this.messages = [...list, ...this.messages]
        } else {
          this.messages = list
        }
      } finally {
        this.loadingMore = false
      }
    },
    connectWs() {
      this.teardownWs()
      const token = getToken()
      if (!token) return
      const base = (VUE_APP_WS_URL || '').replace(/\/$/, '')
      const url = `${base}/cs/user?token=${encodeURIComponent(token)}`
      this.ws = new WebSocket(url)
      this.ws.onmessage = (ev) => this.onWsMessage(ev.data)
      this.ws.onclose = () => this.scheduleReconnect()
      this.pingTimer = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ action: 'ping' }))
        }
      }, 25000)
    },
    scheduleReconnect() {
      if (!this.visible) return
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = setTimeout(() => this.connectWs(), 3000)
    },
    teardownWs() {
      clearInterval(this.pingTimer)
      clearTimeout(this.reconnectTimer)
      if (this.ws) {
        this.ws.close()
        this.ws = null
      }
    },
    onWsMessage(raw) {
      try {
        const envelope = JSON.parse(raw)
        if (envelope.event === 'message' && envelope.data) {
          const msg = envelope.data
          if (msg.sessionId === this.session.id) {
            if (!this.messages.find((m) => m.id === msg.id)) {
              this.messages.push(msg)
              this.$nextTick(() => this.scrollBottom())
            }
            if (msg.senderType === 2) {
              markCsRead({ sessionId: this.session.id })
            }
          }
        }
      } catch (e) {
        console.warn('cs ws parse', e)
      }
    },
    async sendText() {
      const text = (this.draft || '').trim()
      if (!text || !this.session) return
      this.draft = ''
      await this.sendPayload('text', text)
    },
    async sendPayload(msgType, content) {
      if (!this.session || this.sending) return
      this.sending = true
      try {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(
            JSON.stringify({
              action: 'send',
              sessionId: this.session.id,
              msgType,
              content
            })
          )
        } else {
          const res = await sendCsMessage({
            sessionId: this.session.id,
            msgType,
            content
          })
          if (res.result) {
            this.appendLocal(res.result)
          }
        }
      } catch (e) {
        this.$message.error(this.$t('customerService.error'))
      } finally {
        this.sending = false
      }
    },
    appendLocal(msg) {
      if (!this.messages.find((m) => m.id === msg.id)) {
        this.messages.push(msg)
        this.$nextTick(() => this.scrollBottom())
      }
    },
    onImagePick(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      new Compressor(file, {
        quality: 0.8,
        maxWidth: 1280,
        success: (blob) => this.uploadImage(blob),
        error: () => this.uploadImage(file)
      })
    },
    async uploadImage(fileOrBlob) {
      const form = new FormData()
      form.append('file', fileOrBlob, 'cs.jpg')
      try {
        const res = await request({
          url: '/swx/customer/user/upload',
          method: 'post',
          data: form,
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        const url = res.url || (res.result && res.result.url)
        if (url) {
          await this.sendPayload('image', url)
        }
      } catch (e) {
        this.$message.error(this.$t('customerService.error'))
      }
    },
    previewImage(url) {
      window.open(url, '_blank')
    },
    scrollBottom() {
      const el = this.$refs.messageList
      if (el) el.scrollTop = el.scrollHeight
    },
    formatTime(t) {
      if (!t) return ''
      const d = new Date(t)
      const pad = (n) => (n < 10 ? '0' + n : '' + n)
      return `${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
  }
}
</script>

<style lang="scss" scoped>
.cs-root {
  position: fixed;
  z-index: 10000;
}
.cs-root--pc {
  right: 24px;
  bottom: 24px;
  width: 380px;
  height: 520px;
}
.cs-root--mobile {
  inset: 0;
  display: flex;
  align-items: stretch;
  justify-content: center;
}
.cs-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: -1;
}
.cs-panel {
  display: flex;
  flex-direction: column;
  background: #fff;
  color: #000;
  border: 1px solid #000;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}
.cs-root--pc .cs-panel {
  width: 100%;
  height: 100%;
  border-radius: 12px;
}
.cs-root--mobile .cs-panel {
  width: 100%;
  height: calc(100% - 12px);
  margin-top: 12px;
  border-radius: 16px 16px 0 0;
  border: none;
  position: relative;
  z-index: 1;
  overflow: hidden;
}
.cs-root--mobile .cs-header {
  padding: 20px 18px;
  min-height: 56px;
  box-sizing: border-box;
}
.cs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #000;
  color: #fff;
  flex-shrink: 0;
}
.cs-title {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.cs-close {
  border: none;
  background: transparent;
  color: #fff;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}
.cs-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}
.cs-loading {
  text-align: center;
  font-size: 12px;
  color: #888;
  margin-bottom: 8px;
}
.cs-msg-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 14px;
  max-width: 78%;
  width: fit-content;
}
.cs-msg-row--mine {
  align-self: flex-end;
  align-items: flex-end;
  margin-left: auto;
}
.cs-msg-row--other {
  align-self: flex-start;
  align-items: flex-start;
  margin-right: auto;
}
.cs-bubble {
  padding: 10px 12px;
  border-radius: 10px;
  word-break: break-word;
}
.cs-msg-row--mine .cs-bubble {
  background: #000;
  color: #fff;
}
.cs-msg-row--other .cs-bubble {
  background: #fff;
  color: #000;
  border: 1px solid #e0e0e0;
}
.cs-text {
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
}
.cs-image {
  max-width: 200px;
  max-height: 200px;
  border-radius: 6px;
  cursor: pointer;
  display: block;
}
.cs-time {
  font-size: 11px;
  color: #999;
  margin-top: 8px;
  line-height: 1.2;
}
.cs-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #eee;
  background: #fff;
  flex-shrink: 0;
}
.cs-upload-btn {
  width: 40px;
  height: 40px;
  border: 1px solid #000;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 18px;
  font-weight: 300;
  align-self: center;
}
.cs-file {
  display: none;
}
.cs-input {
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px 12px;
  min-height: 40px;
  max-height: 88px;
  resize: none;
  font-size: 14px;
  line-height: 1.4;
  outline: none;
  font-family: inherit;
  box-sizing: border-box;
  align-self: center;
  &:focus {
    border-color: #000;
  }
}
.cs-send {
  height: 40px;
  padding: 0 16px;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  flex-shrink: 0;
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:disabled {
    opacity: 0.5;
  }
}
.cs-fade-enter-active,
.cs-fade-leave-active {
  transition: opacity 0.2s ease;
}
.cs-fade-enter,
.cs-fade-leave-to {
  opacity: 0;
}
</style>
